import { imageSrc } from "@/lib/utils";

export type CarMakeLogoEntry = {
  name: string;
  slug: string;
  optimized: string;
  thumb: string | null;
};

export function buildCarMakeLogoLookup(
  rows: Array<{ name: string; slug?: string | null; optimized?: string | null; thumb?: string | null }>
): Map<string, CarMakeLogoEntry> {
  const lookup = new Map<string, CarMakeLogoEntry>();

  for (const row of rows) {
    const name = row.name?.trim();
    const optimized = imageSrc(row.optimized);
    if (!name || !optimized) continue;

    const entry: CarMakeLogoEntry = {
      name,
      slug: row.slug?.trim().toLowerCase() ?? slugify(name),
      optimized,
      thumb: imageSrc(row.thumb),
    };

    const key = name.toLowerCase();
    const existing = lookup.get(key);
    if (!existing || preferLogo(entry, existing)) {
      lookup.set(key, entry);
    }
  }

  return lookup;
}

function preferLogo(next: CarMakeLogoEntry, current: CarMakeLogoEntry): boolean {
  const nextHosted = isSupabaseUrl(next.optimized);
  const currentHosted = isSupabaseUrl(current.optimized);
  if (nextHosted !== currentHosted) return nextHosted;
  return next.name.length < current.name.length;
}

function isSupabaseUrl(url: string): boolean {
  return url.includes("supabase.co");
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function resolveCarMakeLogo(make: string, lookup: Map<string, CarMakeLogoEntry>): CarMakeLogoEntry | null {
  const trimmed = make.trim();
  if (!trimmed) return null;

  const exact = lookup.get(trimmed.toLowerCase());
  if (exact) return exact;

  const slug = slugify(trimmed);
  for (const entry of lookup.values()) {
    if (entry.slug === slug) return entry;
  }

  const normalized = normalizeMake(trimmed);
  let best: CarMakeLogoEntry | null = null;

  for (const entry of lookup.values()) {
    const entryName = entry.name.toLowerCase();
    const matches =
      entryName === normalized ||
      entryName.startsWith(`${normalized} `) ||
      normalized.startsWith(entryName);

    if (!matches) continue;
    if (!best || entry.name.length < best.name.length) best = entry;
  }

  return best;
}

function normalizeMake(value: string): string {
  return value.trim().toLowerCase();
}

export function logoSources(entry: CarMakeLogoEntry | null): string[] {
  if (!entry) return [];
  return [entry.optimized, entry.thumb].filter((src): src is string => Boolean(src));
}
